import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import { AMAZON_URL, AMAZON_EDITIONS, BOOK } from "@/data/book";

export const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden pt-36 pb-24 sm:pt-44 sm:pb-32"
  >
    {/* ambient watercolor glow */}
    <div className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-24 w-[38rem] h-[38rem] rounded-full bg-[#2a7b76]/10 blur-[120px]" />
      <div className="absolute top-40 -left-24 w-[30rem] h-[30rem] rounded-full bg-[#c07a67]/10 blur-[110px]" />
    </div>

    <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
      {/* copy */}
      <div className="lg:col-span-7 order-2 lg:order-1">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-xs uppercase tracking-[0.28em] text-[#2a7b76] font-semibold mb-6"
        >
          {BOOK.eyebrow}
        </motion.p>

        <motion.h1
          data-testid="hero-title"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
          className="font-display font-light text-[#201c19] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
        >
          Ascent of the
          <span className="block italic text-[#2a7b76]">Hummingbird</span>
        </motion.h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="mt-7 max-w-xl font-display text-2xl sm:text-3xl italic text-[#4b443e] leading-snug"
        >
          {BOOK.hook}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.32 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={AMAZON_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-buy-amazon-btn"
            className="group inline-flex items-center gap-2.5 rounded-full bg-[#c2751f] text-white text-base font-semibold px-8 py-4 shadow-[0_10px_30px_rgba(194,117,31,0.28)] hover:bg-[#a9631a] transition-colors duration-300"
          >
            Buy on Amazon
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
          </a>
          <a
            href="#poems"
            data-testid="hero-sample-poem-btn"
            className="inline-flex items-center gap-2 rounded-full border border-stone-300 text-[#201c19] text-base font-medium px-7 py-4 hover:border-[#2a7b76] hover:text-[#2a7b76] transition-colors duration-300"
          >
            <BookOpen size={18} />
            Read a poem
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="mt-6 text-sm text-stone-500"
        >
          Available in{" "}
          {AMAZON_EDITIONS.map((e, i) => (
            <span key={e.label}>
              <a
                href={e.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`hero-edition-${e.label.toLowerCase()}`}
                className="text-[#2a7b76] underline underline-offset-2 decoration-stone-300 hover:decoration-[#2a7b76] transition-colors"
              >
                {e.label}
              </a>
              {i < AMAZON_EDITIONS.length - 1 ? <span className="mx-1.5 text-stone-300">·</span> : null}
            </span>
          ))}
        </motion.p>
      </div>

      {/* cover */}
      <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.94, rotate: -2 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="animate-floaty"
        >
          <img
            data-testid="hero-book-cover"
            src={BOOK.coverImage}
            alt={`${BOOK.title} cover by ${BOOK.author}`}
            className="w-[260px] sm:w-[320px] lg:w-[360px] rounded-[6px] bg-white ring-1 ring-stone-200/70 shadow-[0_30px_60px_-15px_rgba(42,123,118,0.28),0_18px_36px_-18px_rgba(32,28,25,0.25)]"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

export default Hero;
